import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useMutation, useQuery } from "react-query";
import getPkce from "oauth-pkce";
import { jwtDecode, JwtPayload } from "jwt-decode";

import { Header } from "@kitbird/core";
import BaseLoading from "../Loading/BaseLoading";
import AuthContext from "./authContext";

import { getToken, getUserInfo } from "../../services/common/authService";
import { getRBACConfig, getRBACRoles, getRBACAppByAppId } from "../../services/common/rbacService";
import { transformQueryStringToObject, redirectToSSO, logoutUser } from "./utils";
import { usePersistedState } from "../../hooks";
import { TokenRequestType } from "../../services/common/types";

import { CLIENT_ID, DASHBOARD_URL, CLIENT_SECRET } from "../../services/config";
import { RBACMenu, UserPoolI, UserRole } from "./auth.type";
import { getUserAccount } from "../../services/userAccountService";
import { HeaderWrapper } from "../FloatHeader/FloatHeader.style";

const CenterContainerDiv = styled.div`
  width: 100vw;
  height: 100vh;
  display: grid;
  place-items: center;
`;

const AuthProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [user, setUser] = usePersistedState("USER_INFO", "");
  const [userRoleId, setUserRoleId] = usePersistedState("USER_ROLE", "");
  const [codeVerifier, setCodeVerifier] = usePersistedState("CODE_VERIFIER", "");
  const [userAccount, setUserAccount] = usePersistedState("USER_ACCOUNT", "");
  const [isLoadingLogout, setIsLoadingLogout] = useState<boolean>(false);

  const userInfo = useMemo<Record<string, any> | undefined>(() => {
    if (!user) return undefined;

    const userInfoTemp: Record<string, any> = jwtDecode<JwtPayload>(user.access_token);
    return userInfoTemp;
  }, [user]);

  const {
    data: rbacRoles,
    isLoading: rbacRoleLoading,
    // isError: isErrorRbacRoles,
  } = useQuery(
    ["rbacRoles"],
    getRBACRoles,
    {
      enabled: !!userInfo && !!user,
    },
  );

  useEffect(() => {
    if (rbacRoles?.data?.roles?.length > 0) {
      setUserRoleId(rbacRoles.data.roles[0].id);
    }
  }, [rbacRoles]);

  const {
    data: rbacMenu,
    isLoading: rbacLoading,
    // isError: isErrorRbacMenu,
  } = useQuery(
    ["rbac", userRoleId],
    () => getRBACConfig(userRoleId),
    {
      enabled: !!userInfo && !!user && !!userRoleId,
    },
  );

  const {
    data: appInfo,
    isLoading: appInfoLoading,
    // isError: isErrorAppInfo,
  } = useQuery(
    ["appInfo"],
    getRBACAppByAppId,
    {
      enabled: !!userInfo && !!user,
    },
  );

  const {
    data: dataUserInfo,
    isLoading: isUserInfoLoading,
    refetch: refetchUserInfo,
  } = useQuery({
    queryKey: ["get_user_info", user],
    enabled: !!user,
    queryFn: () => getUserInfo(user.access_token),
  });

  const {
    data: dataUserAccount,
    isLoading: isUserAccountLoading,
  } = useQuery({
    queryKey: ["get_user_account", dataUserInfo, userRoleId],
    enabled: !!dataUserInfo && !!dataUserInfo.email && !!userRoleId,
    queryFn: () => (
      dataUserInfo && dataUserInfo.email
        ? getUserAccount(dataUserInfo.email)
        : undefined
    ),
  });

  const getTokenMutation = useMutation({
    mutationFn: (data: TokenRequestType) => getToken(data),
    onSuccess: (responseDataToken) => {
      if (responseDataToken) {
        setUser(responseDataToken);
        setCodeVerifier("");
        refetchUserInfo();
      }
    },
  });

  useEffect(() => {
    if (dataUserAccount) {
      const userDepartments: string[] = [];
      const userPools: UserPoolI[] = [];
      const uniquePoolIds = new Set(
        dataUserAccount.position_info.map((item) => item.pool_id),
      );
      uniquePoolIds.forEach((poolId) => {
        const newPool: UserPoolI = {
          poolName: "",
          poolId: "",
          departments: [],
        };
        dataUserAccount.position_info.forEach((item) => {
          if (item.pool_id === poolId) {
            newPool.poolName = item.pool_name;
            newPool.poolId = item.pool_id;
            newPool.departments.push(item.department);
          }
        });
        newPool.departments = Array.from(new Set(newPool.departments));
        userDepartments.push(...newPool.departments);
        userPools.push(newPool);
      });
      setUserAccount({
        ...dataUserAccount,
        userPools,
        userDepartments: Array.from(new Set(userDepartments)),
      });
    }
  }, [dataUserAccount]);

  const clearStorage = () => {
    try {
      setUser("");
      setUserRoleId("");
    } catch (err) {
      console.error("Error Clear Storage");
    }
  };

  const generatePKCE = async () => {
    const pkce: { verifier: string, challenge: string } = await new Promise((resolve) => {
      getPkce(43, (error, { verifier, challenge }) => {
        if (error) throw error;
        resolve({ verifier, challenge });
      });
    });

    setCodeVerifier(pkce.verifier);
    redirectToSSO(pkce.challenge);
  };

  // to prevent react strictMode call useEffect twice in local/development mode
  const [init, setInit] = useState<boolean>(false);
  useEffect(() => {
    if (!init) setInit(true);
  }, []);

  useEffect(() => {
    if (
      new URL(window.location.href).href.includes("&code=")
      && window.location.pathname === "/"
    ) {
      const queryString: {
        code: string;
        scope: string;
        session_state: string
      } = transformQueryStringToObject(window.location.search);

      if (queryString.code && codeVerifier && !user && init) {
        getTokenMutation.mutate({
          client_id: CLIENT_ID,
          redirect_uri: DASHBOARD_URL,
          grant_type: "authorization_code",
          code: queryString.code ?? "",
          code_verifier: codeVerifier,
          client_secret: CLIENT_SECRET,
        });
      } else if (init) {
        window.location.assign("/");
      }
    } else if (!user) {
      window.localStorage.setItem("LAST_PATHNAME", window.location.pathname);
      generatePKCE();
    }
  }, [init]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // eslint-disable-next-line no-param-reassign
      event.returnValue = ""; // This line is required for Chrome compatibility
      event.stopImmediatePropagation();

      clearStorage();
    };

    window.addEventListener("unload", handleBeforeUnload);

    return () => {
      window.removeEventListener("unload", handleBeforeUnload);
    };
  }, []);

  const logout = async () => {
    setIsLoadingLogout(true);
    clearStorage();

    try {
      const tokenId = user.id_token;
      await logoutUser(tokenId);
    } catch (error) {
      console.error(error);
    }
  };

  const rbacInfo = useMemo<RBACMenu[] | undefined>(() => rbacMenu?.data?.menus, [rbacMenu]);
  const isLoading = rbacLoading
    || rbacRoleLoading
    || !user
    || isLoadingLogout
    || appInfoLoading
    || !dataUserInfo
    || isUserInfoLoading
    || isUserAccountLoading;

  const isUserAcountNotFound = !dataUserAccount;

  if (isLoading && isUserAcountNotFound) {
    return (
      <CenterContainerDiv>
        <BaseLoading />
      </CenterContainerDiv>
    );
  }
  if (!isLoading && isUserAcountNotFound) {
    return (
      <CenterContainerDiv>
        <HeaderWrapper>
          <Header
            handleLogout={() => logout()}
          />
        </HeaderWrapper>
        User tidak ditemukan, harap login dengan akun lain
      </CenterContainerDiv>
    );
  }

  return (
    <AuthContext.Provider value={{
      user,
      userInfo: { ...userInfo, email: dataUserInfo.email },
      userAccount,
      rbacInfo: rbacInfo || [],
      rbacRoles: rbacRoles?.data?.roles.map((role: UserRole) => role.name) || [],
      appInfo: appInfo?.data,
      logout,
    }}>
      {
        children
      }
    </AuthContext.Provider>
  );
};

export default AuthProvider;
